/**
 * Service definition schemas: the TypeScript mirror of the Python
 * `socaity_schemas.service_definitions` module. Field names are snake_case so
 * definitions serialized by the Socaity backend (pydantic) parse 1:1.
 */

export type ParameterLocation = 'query' | 'path' | 'header' | 'cookie' | 'body';

export type ParameterType =
  | 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array' | 'null';

/** `file`, `image`, `video`, `audio` mark media-toolkit params; `binary`/`uri` come from OpenAPI. */
export type ParameterFormat = 'file' | 'image' | 'video' | 'audio' | 'uri' | 'binary' | string;

export interface ParameterDefinition {
  type?: ParameterType;
  format?: ParameterFormat;
  enum?: any[];
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  additional_properties?: boolean | Record<string, any>;
}

export interface EndpointParameter {
  name: string;
  /** Merged anyOf/oneOf definitions; an array means "any of these". */
  definition?: ParameterDefinition | ParameterDefinition[];
  required?: boolean;
  default?: any;
  location: ParameterLocation;
  param_schema?: Record<string, any>;
  description?: string;
}

export interface EndpointDefinition {
  id?: string;
  display_name?: string;
  description?: string;
  short_desc?: string;
  path: string;
  parameters: EndpointParameter[];
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD' | 'OPTIONS';
  /** OpenAPI requestBody MIME type, e.g. application/json or multipart/form-data. */
  request_body_content_type?: string;
  /** True when the request schema exposes a boolean `stream` flag. */
  supports_streaming?: boolean;
  /** Hint from the spec (x-timeout) how long a request may take, in seconds. */
  timeout_hint_s?: number;
  responses?: Record<string, any>;
}

export interface ServiceAddress {
  base_url: string;
  path?: string;
  job_path?: string;
}

export interface ServiceDefinition {
  id?: string;
  display_name?: string;
  description?: string;
  short_desc?: string;
  endpoints: EndpointDefinition[];
  /** `socaity` | `apipod` | `openapi` | ... Drives status-link defaults. */
  specification?: string;
  service_address?: ServiceAddress;
  version?: string;
}

// ---------------------------------------------------------------------------
// ServiceAddress helpers (port of the pydantic computed fields)
// ---------------------------------------------------------------------------

const stripSlashes = (value: string): string => value.replace(/^\/+|\/+$/g, '');

/** `base_url` + optional path segments, normalized to no trailing slash. */
export function composeUrl(address: ServiceAddress, ...segments: (string | undefined)[]): string {
  const parts = [address.path, ...segments]
    .map((segment) => stripSlashes(segment ?? ''))
    .filter(Boolean);
  const base = address.base_url.replace(/\/+$/, '');
  return parts.length ? `${base}/${parts.join('/')}` : base;
}

/** Full URL for an endpoint path of this service. */
export function buildEndpointUrl(address: ServiceAddress, endpointPath: string): string {
  return composeUrl(address, endpointPath);
}

/**
 * Resolve a (possibly relative) link from a job response against the service
 * address. Absolute URLs pass through; `/rooted` paths resolve against the
 * base URL origin; bare paths resolve against the service URL.
 */
export function resolveUrl(address: ServiceAddress, url: string): string {
  if (/^https?:\/\//.test(url)) return url;
  if (url.startsWith('/')) {
    const origin = new URL(address.base_url).origin;
    return `${origin}${url}`;
  }
  return `${composeUrl(address)}/${stripSlashes(url)}`;
}

/** Parse a plain URL string into a ServiceAddress. */
export function serviceAddressFromUrl(url: string): ServiceAddress {
  const parsed = new URL(url);
  return {
    base_url: parsed.origin,
    path: stripSlashes(parsed.pathname),
  };
}

const FILE_FORMATS: ReadonlySet<string> = new Set(['file', 'image', 'video', 'audio', 'binary']);

/** True when the parameter expects an uploadable media file. */
export function isFileParameter(param: EndpointParameter): boolean {
  const definitions = param.definition == null
    ? []
    : Array.isArray(param.definition) ? param.definition : [param.definition];
  return definitions.some((definition) => !!definition.format && FILE_FORMATS.has(definition.format));
}
